/**
 * The review queue — every lead waiting on a person, oldest first.
 *
 * Visual Reference: `design/screens/review-queue.html`, four states — default,
 * loading, empty, error.
 *
 * PRD §7.4: "**Review queue** | Confidence-gated escalations (§3.8),
 * oldest-first | Reviewer, Admin, Owner | M2". Story 22.2 records the queue on
 * the lead and `GET /v1/w/{wid}/review-queue` reads it back, refusing every
 * other role AT THE API — which is where that is tested
 * (`packages/api/test/review-queue.test.ts`), not here.
 *
 * THE ORDER IS THE QUEUE. There is no sort control because the mockup draws
 * none, and the manifest says why: oldest first is the order, not a default.
 *
 * A SENTENCE FIRST (PRD §1.9). "Four leads are waiting on you." counts the whole
 * queue — the API's `waiting` — and not what the filters leave, because a filter
 * narrows the list; it does not change how many leads are waiting on a person.
 *
 * WHAT THIS DRAWS THAT THE MOCKUP DOES NOT, and why each one:
 *
 *   · **Show more** — the API pages at 25 and the mockup draws four rows. The
 *     goal is a Reviewer who sees every lead waiting, so the 26th is reachable.
 *   · **No waiting lead matches these filters** — the mockup's empty state means
 *     the QUEUE is empty. A filter that leaves nothing is a different sentence,
 *     with `Clear filters` as its way back.
 *   · **The over-cap row** — the leads list's treatment, for the leads list's
 *     reason: `GET /leads/{lid}` 404s over the cap, so there is no drawer to open
 *     and no `Review` control to draw. The row stays, because the visible row is
 *     the proof of the claim (DECISION-015).
 *
 * Story 22.3.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import type { Escalation } from '@leadgible/schema'
import { apiGet, ApiFailure } from '../lib/api'
import { relative } from '../lib/relative'
import { capitalise, spell } from '../lib/words'
import { BILLING_PATH } from '../shell/settings-nav'
import { useShell } from '../shell/workspace'
import { Button } from '../ui/Button'
import { Pill } from '../ui/Pill'
import { Select } from '../ui/Select'
import { Skeleton } from '../ui/Skeleton'
import { AVATAR, PILL_TONE, initials } from './Leads'
import { leadName } from '../lib/lead-name'

/** One row, as `GET /v1/w/{wid}/review-queue` serialises it through the seam. */
type QueueLead = {
  id: string
  contactName?: string | null
  contactCompany?: string | null
  funnelId: string
  funnelName: string | null
  /** Absent over the cap — DECISION-015. */
  score?: number | null
  tone: 'hot' | 'review' | 'nurture' | null
  bandLabel: string | null
  overCap: boolean
  escalatedAt: number
  /** Why it is here. Absent over the cap, withheld with the verdict. */
  escalation?: Escalation | null
}

type QueueView = {
  leads: QueueLead[]
  nextCursor?: string
  /** Waiting AND matching the filters. */
  total: number
  /** Waiting, whatever the filters. */
  waiting: number
  funnels: { id: string; name: string }[]
}

/**
 * The export's words. Straight apostrophes, product-wide (DECISION-118) — and
 * held here rather than in JSX text, where a straight one is a lint error.
 */
const COPY = {
  sentence:
    "The model wasn't confident enough to decide these on its own. Oldest first — the longest wait goes first.",
  sentenceOne: "The model wasn't confident enough to decide this one on its own.",
  emptySentence: 'Every lead the model was sure about has already gone where it was meant to go.',
  emptyTitle: 'Nothing is waiting on you.',
  emptyBody:
    "When the model isn't confident about a lead, it comes here instead of being routed automatically. Leads you have already decided don't come back.",
  errorSentence: 'The queue itself is fine — this view just failed to fetch it.',
  errorTitle: "Couldn't load your review queue",
  errorBody: 'Nothing has been lost — every lead is still waiting. Load the queue again.',
  rule: 'A lead below your confidence threshold waits here whatever it scored.',
  noMatch: 'No waiting lead matches these filters',
} as const

/** "Four leads are waiting on you." — small numbers as words, the probe's rule. */
const headline = (waiting: number) =>
  `${capitalise(spell(waiting))} ${waiting === 1 ? 'lead is' : 'leads are'} waiting on you.`

/**
 * "48%". FLOORED, because the gate is `confidence < threshold / 100`: a lead
 * queued at 0.695 against 70 must not read "70%" beside the rule that queued it.
 * Through a tenth first, because `0.29 * 100` is `28.999999999999996` — the float
 * Story 22.2 met — and a bare floor would print 28.
 */
const percent = (confidence: number) => Math.floor(Math.round(confidence * 1000) / 10)

/** Why this lead is here, in the mockup's three shapes — plus a plural it does not draw. */
function reasonText(escalation: Escalation): string {
  const low = escalation.reasons.includes('low_confidence')
  const flagged = escalation.reasons.includes('coherence_flag')
  const confidence =
    escalation.confidence === null
      ? 'Low confidence'
      : `Low confidence — ${percent(escalation.confidence)}%`
  const flags =
    escalation.flags > 1 ? `${spell(escalation.flags)} coherence flags` : 'a coherence flag'
  if (low && flagged) return `${confidence} · and ${flags}`
  if (low) return confidence
  return capitalise(flags)
}

/**
 * "4 days", "yesterday", "3 hours" — how long it has waited.
 *
 * `lib/relative.ts` without its "ago": the mockup draws a WAIT rather than a
 * moment, and a second copy of the boundaries is exactly the drift that file was
 * lifted out to prevent.
 */
const waited = (at: number, now: number) => relative(at, now).replace(/ ago$/, '')

function queuePath(workspaceId: string, query: string, cursor?: string): string {
  const params = new URLSearchParams(query)
  if (cursor) params.set('cursor', cursor)
  const search = params.toString()
  return `/v1/w/${workspaceId}/review-queue${search ? `?${search}` : ''}`
}

export function ReviewQueue() {
  const { workspace } = useShell()
  const workspaceId = workspace?.id ?? null

  const [view, setView] = useState<QueueView | null>(null)
  const [error, setError] = useState<string | null>(null)
  // Kept apart from the view, so the Funnel select keeps its options while a
  // filter change reloads the queue — an option list that empties mid-choice
  // resets the choice.
  const [funnels, setFunnels] = useState<QueueView['funnels']>([])
  const [funnel, setFunnel] = useState('')
  const [reason, setReason] = useState('')
  const [band, setBand] = useState('')
  const [paging, setPaging] = useState(false)

  /** The filters, as the query string the API reads. */
  const query = useMemo(() => {
    const params = new URLSearchParams()
    if (funnel) params.set('funnel', funnel)
    if (reason) params.set('reason', reason)
    if (band) params.set('band', band)
    return params.toString()
  }, [funnel, reason, band])

  const load = useCallback(async () => {
    if (!workspaceId) return
    setError(null)
    setView(null)
    try {
      const next = await apiGet<QueueView>(queuePath(workspaceId, query))
      setView(next)
      setFunnels(next.funnels)
    } catch (e) {
      // A message from the API names the fix and is rendered as it arrives
      // (ARCH §4.3); anything else gets the export's own reassurance.
      setError(e instanceof ApiFailure ? e.message : COPY.errorBody)
    }
  }, [workspaceId, query])

  useEffect(() => {
    void load()
  }, [load])

  const showMore = async () => {
    if (!workspaceId || !view?.nextCursor || paging) return
    setPaging(true)
    try {
      const next = await apiGet<QueueView>(queuePath(workspaceId, query, view.nextCursor))
      setView((current) => (current ? { ...next, leads: [...current.leads, ...next.leads] } : next))
    } catch (e) {
      setError(e instanceof ApiFailure ? e.message : COPY.errorBody)
    } finally {
      setPaging(false)
    }
  }

  const clearFilters = () => {
    setFunnel('')
    setReason('')
    setBand('')
  }

  const now = Date.now()
  const empty = view !== null && view.waiting === 0
  const sentence = 'm-0 max-w-mdx-measure-52 text-mdx-18 leading-mdx-reason text-mdx-slate'

  return (
    <div className="flex flex-col gap-mdx-24">
      <header className="flex flex-col gap-mdx-10">
        <span className="text-mdx-12 font-mdx-bold uppercase tracking-mdx-eyebrow text-mdx-slate">
          {workspace?.name ?? 'This workspace'} · review queue
        </span>
        {/* The export draws an H1 in the default state only. Empty and error lead
            with the sentence alone, as the dashboard's error state does. */}
        {error ? (
          <p className={sentence}>{COPY.errorSentence}</p>
        ) : view === null ? (
          <>
            <Skeleton className="h-mdx-40 rounded-mdx-control-sm" style={{ width: '40%' }} />
            <Skeleton className="h-mdx-18 rounded-mdx-control-sm" style={{ width: '48%' }} />
          </>
        ) : empty ? (
          <p className={sentence}>{COPY.emptySentence}</p>
        ) : (
          <>
            <h1 className="m-0 text-balance text-mdx-verdict font-mdx-bold leading-mdx-verdict tracking-mdx-verdict text-mdx-ink">
              {headline(view.waiting)}
            </h1>
            <p className={sentence}>{view.waiting === 1 ? COPY.sentenceOne : COPY.sentence}</p>
          </>
        )}
      </header>

      {error || empty ? null : (
        <div className="flex flex-wrap items-end gap-mdx-12">
          <Select
            label="Funnel"
            showLabel
            value={funnel}
            onChange={(e) => setFunnel(e.target.value)}
            className="basis-mdx-filter-column"
          >
            <option value="">All funnels</option>
            {funnels.map((option) => (
              <option key={option.id} value={option.id}>
                {option.name}
              </option>
            ))}
          </Select>
          <Select
            label="Why it's here"
            showLabel
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className="basis-mdx-filter-column"
          >
            <option value="">Any reason</option>
            <option value="low_confidence">Low confidence</option>
            <option value="coherence_flag">A coherence flag</option>
          </Select>
          <Select
            label="Band"
            showLabel
            value={band}
            onChange={(e) => setBand(e.target.value)}
            className="basis-mdx-row-control-sm"
          >
            <option value="">All bands</option>
            <option value="hot">Hot</option>
            <option value="review">Review</option>
            <option value="nurture">Nurture</option>
          </Select>
          {/* Held back while loading: a count for a list that has not arrived
              is a number the screen does not have. */}
          {view ? (
            <span className="pb-mdx-10 text-mdx-15 text-mdx-slate tabular-nums">
              {view.total.toLocaleString()} of {view.waiting.toLocaleString()}
            </span>
          ) : null}
        </div>
      )}

      <div className="flex flex-col gap-mdx-2 rounded-mdx-card border border-mdx-line bg-mdx-white p-mdx-12 shadow-mdx-card">
        {error ? (
          <Notice
            mark="error"
            title={COPY.errorTitle}
            body={error}
            action="Try again"
            onAction={() => void load()}
          />
        ) : view === null ? (
          <LoadingRows />
        ) : empty ? (
          <Notice
            title={COPY.emptyTitle}
            body={COPY.emptyBody}
            action="Set your confidence threshold"
            // The threshold is per funnel (Story 20.4) and this page spans every
            // funnel, so the drawing names no one destination. The funnels list
            // is the one place every threshold is reached from — the manifest
            // records the resolution.
            onAction={() => window.location.assign('/funnels')}
          />
        ) : view.leads.length === 0 ? (
          <Notice title={COPY.noMatch} action="Clear filters" secondary onAction={clearFilters} />
        ) : (
          <>
            <div role="list" aria-label="Leads waiting on you" className="flex flex-col gap-mdx-2">
              {view.leads.map((lead) => (
                <QueueRow key={lead.id} lead={lead} now={now} />
              ))}
            </div>
            {view.nextCursor ? (
              <div className="flex justify-center px-mdx-16 py-mdx-8">
                <Button
                  variant="secondary"
                  size="small"
                  loading={paging ? 'Loading the next leads' : undefined}
                  onClick={() => void showMore()}
                >
                  Show more
                </Button>
              </div>
            ) : null}
            <Rule leads={view.leads} />
          </>
        )}
      </div>
    </div>
  )
}

/**
 * One row — the leads list's avatar, pill and over-cap treatment, with the
 * queue's own middle: why it is here, and how long it has waited.
 *
 * The flex bases are what make it WRAP at 375 rather than squeeze, the lesson
 * `LeadRow` records. The export's 190px and 230px take the nearest role tokens,
 * `--mdx-row-identity` (200) and `--mdx-row-control` (210), both listed as
 * deviations. Its 84px age column is `--mdx-meta-column-sm`, the role DECISION-147
 * named for the numeric slot three M2 screens draw at 84 — it was
 * `--mdx-meta-column` (96) until then, the third listed deviation.
 */
function QueueRow({ lead, now }: { lead: QueueLead; now: number }) {
  const name = leadName(lead.contactName)
  const meta = [lead.contactCompany, lead.funnelName].filter(Boolean).join(' · ')
  // `from=queue` sends the drawer's close back here, over this page (Story 22.4):
  // a Reviewer working the queue decides a lead and returns to the next one.
  const drawer = `/leads/${lead.id}?from=queue`

  return (
    <div
      role="listitem"
      className={[
        'flex flex-wrap items-center gap-x-mdx-16 gap-y-mdx-10 rounded-mdx-control px-mdx-16 py-mdx-14',
        'transition-colors ease-mdx-swift',
        lead.overCap ? 'bg-mdx-ground' : 'hover:bg-mdx-ground',
      ].join(' ')}
    >
      <span
        aria-hidden="true"
        className={[
          'flex size-mdx-avatar-sm flex-none items-center justify-center rounded-mdx-pill',
          'text-mdx-12 font-mdx-bold text-mdx-white',
          lead.tone ? AVATAR[lead.tone] : 'bg-mdx-nurture',
          lead.overCap ? 'opacity-mdx-withheld' : '',
        ].join(' ')}
      >
        {initials(lead.contactName)}
      </span>

      <span className="flex min-w-0 grow basis-mdx-row-identity flex-col leading-mdx-heading">
        {lead.overCap ? (
          <span className="self-start text-mdx-15 font-mdx-semibold text-mdx-slate">{name}</span>
        ) : (
          <a
            href={drawer}
            className="self-start rounded-mdx-4 text-mdx-15 font-mdx-semibold text-mdx-ink no-underline transition-colors ease-mdx-swift hover:text-mdx-brand-deep"
          >
            {name}
          </a>
        )}
        {meta ? <span className="text-mdx-14 text-mdx-slate">{meta}</span> : null}
      </span>

      {lead.overCap ? (
        <span className="flex min-w-0 grow basis-mdx-row-control flex-wrap items-center gap-mdx-10">
          <Button
            variant="ghost"
            size="small"
            className="whitespace-nowrap"
            onClick={() => window.location.assign(BILLING_PATH)}
          >
            Upgrade to see details
          </Button>
          <span className="text-mdx-13 text-mdx-slate">Captured and scored, beyond your plan cap</span>
        </span>
      ) : (
        <span className="flex min-w-0 grow basis-mdx-row-control items-center gap-mdx-8 text-mdx-15 text-mdx-ink">
          {lead.escalation ? <ReasonMarks escalation={lead.escalation} /> : null}
          <span className="min-w-0">{lead.escalation ? reasonText(lead.escalation) : '—'}</span>
        </span>
      )}

      <span className="flex flex-none items-center gap-mdx-10">
        {/* The score column is reserved over the cap and says nothing, as the
            leads list's does: the width is layout, the number is the plan's. */}
        <span
          aria-hidden={typeof lead.score !== 'number'}
          className="w-mdx-icon-button text-right text-mdx-15 font-mdx-bold text-mdx-ink tabular-nums"
        >
          {typeof lead.score === 'number' ? lead.score : ''}
        </span>
        <Pill tone={lead.overCap ? 'neutral' : lead.tone ? PILL_TONE[lead.tone] : 'neutral'} dot>
          {lead.overCap ? 'Hidden' : (lead.bandLabel ?? 'Unbanded')}
        </Pill>
      </span>

      <span className="w-mdx-meta-column-sm flex-none whitespace-nowrap text-right text-mdx-14 text-mdx-slate">
        {waited(lead.escalatedAt, now)}
      </span>

      {lead.overCap ? null : (
        <Button
          variant="secondary"
          size="small"
          aria-label={`Review ${name}`}
          className="flex-none"
          onClick={() => window.location.assign(drawer)}
        >
          Review
        </Button>
      )}
    </div>
  )
}

/** The reason's marks: grey for low confidence, amber for a flag, both overlapping. */
function ReasonMarks({ escalation }: { escalation: Escalation }) {
  const low = escalation.reasons.includes('low_confidence')
  const flagged = escalation.reasons.includes('coherence_flag')
  return (
    <>
      {low ? (
        <span aria-hidden="true" className="size-mdx-16 flex-none rounded-mdx-5 bg-mdx-nurture" />
      ) : null}
      {flagged ? (
        <span
          aria-hidden="true"
          className={['size-mdx-16 flex-none rounded-mdx-5 bg-mdx-review', low ? '-ml-mdx-4' : ''].join(' ')}
        />
      ) : null}
    </>
  )
}

/**
 * PRD §3.8's rule, stated under the list — and, when a Hot lead is waiting on low
 * confidence, said about that lead, because she is the case the rule exists for.
 *
 * "Send them on" where the export says "her": a respondent's pronouns are not
 * something the product knows.
 */
function Rule({ leads }: { leads: QueueLead[] }) {
  const hot = leads.find(
    (lead) =>
      !lead.overCap &&
      lead.tone === 'hot' &&
      Boolean(lead.contactName?.trim()) &&
      lead.escalation?.reasons.includes('low_confidence'),
  )
  const first = hot?.contactName?.trim().split(/\s+/)[0]
  return (
    <p className="m-0 max-w-mdx-measure px-mdx-16 pb-mdx-6 pt-mdx-14 text-mdx-14 text-mdx-slate">
      {COPY.rule}
      {hot && first
        ? ` ${first} cleared your ${hot.bandLabel ?? 'Hot'} band — the model just wasn't sure enough to send them on.`
        : null}
    </p>
  )
}

/** The empty, error and filtered-to-nothing states share one shape in the export. */
function Notice({
  title,
  body,
  action,
  onAction,
  mark = 'plain',
  secondary = false,
}: {
  title: string
  body?: string
  action: string
  onAction: () => void
  mark?: 'plain' | 'error'
  secondary?: boolean
}) {
  return (
    <div className="flex flex-col items-center gap-mdx-14 px-mdx-16 py-mdx-48 text-center">
      {mark === 'error' ? (
        <span
          aria-hidden="true"
          className="flex size-mdx-40 flex-none items-center justify-center rounded-mdx-control bg-mdx-error text-mdx-20 font-mdx-bold text-mdx-white"
        >
          !
        </span>
      ) : (
        <span aria-hidden="true" className="size-mdx-40 flex-none rounded-mdx-control bg-mdx-line" />
      )}
      <span className="text-mdx-17 font-mdx-semibold tracking-mdx-h3 text-mdx-ink">{title}</span>
      {body ? <span className="max-w-mdx-measure-56 text-mdx-15 text-mdx-slate">{body}</span> : null}
      <Button variant={secondary ? 'secondary' : 'primary'} className="mt-mdx-2" onClick={onAction}>
        {action}
      </Button>
    </div>
  )
}

/** Four skeleton rows at the real row height, so nothing jumps when the queue lands. */
function LoadingRows() {
  return (
    <div className="flex flex-col gap-mdx-2" aria-busy="true">
      {(['58%', '50%', '62%', '54%'] as const).map((width) => (
        <div key={width} className="flex flex-wrap items-center gap-x-mdx-16 gap-y-mdx-10 px-mdx-16 py-mdx-14">
          <Skeleton className="size-mdx-avatar-sm flex-none rounded-mdx-pill" />
          <span className="flex min-w-0 grow basis-mdx-row-identity flex-col gap-mdx-6">
            <Skeleton className="h-mdx-14 rounded-mdx-sm" style={{ width }} />
            <Skeleton className="h-mdx-12 rounded-mdx-sm" style={{ width: '78%' }} />
          </span>
          <Skeleton className="h-mdx-14 min-w-0 grow basis-mdx-row-control rounded-mdx-sm" />
          <Skeleton className="h-mdx-24 w-mdx-meta-column flex-none rounded-mdx-pill" />
          <Skeleton className="h-mdx-14 w-mdx-meta-column-sm flex-none rounded-mdx-sm" />
          <Skeleton className="h-mdx-icon-button w-mdx-meta-column flex-none rounded-mdx-control-sm" />
        </div>
      ))}
    </div>
  )
}
